import {Tooltip} from 'antd';
import moment from 'moment';
import {Money} from './Money';
import styles from './ordersMetrics.module.scss';

/**
 * The collapsible bodies of the metrics panel.
 *
 * Each section encodes a rule from the integration doc that, ignored, makes
 * the screen state something untrue. The rules are noted where they apply.
 */

const count = value =>
  value == null ? '—' : new Intl.NumberFormat().format(value);

const quantity = value =>
  value == null
    ? '—'
    : new Intl.NumberFormat(undefined, {maximumFractionDigits: 4}).format(
        value,
      );

/** Milliseconds as something a human reads at a glance. */
export const duration = ms => {
  if (ms == null) return '—';
  const seconds = ms / 1000;
  if (seconds < 90) return `${seconds.toFixed(seconds < 10 ? 1 : 0)}s`;
  const minutes = seconds / 60;
  if (minutes < 90) return `${minutes.toFixed(0)}m`;
  const hours = minutes / 60;
  if (hours < 48) return `${hours.toFixed(1)}h`;
  return `${(hours / 24).toFixed(1)}d`;
};

/**
 * The same duration spelled out, for places with room for words.
 *
 * "9.9d" is precise and unreadable; nobody waits nine-point-nine days.
 */
export const durationLong = ms => {
  if (ms == null) return null;
  const minutes = ms / 60000;
  if (minutes < 1) return 'under a minute';
  if (minutes < 60)
    return `${Math.round(minutes)} minute${
      Math.round(minutes) === 1 ? '' : 's'
    }`;
  const hours = minutes / 60;
  if (hours < 48)
    return `${Math.round(hours)} hour${Math.round(hours) === 1 ? '' : 's'}`;
  const days = Math.round(hours / 24);
  return `${days} day${days === 1 ? '' : 's'}`;
};

/**
 * How long the longest-outstanding fill has been waiting — or why that cannot
 * be said.
 *
 * `oldestUnsettledMs` comes back as exactly 0 on some scopes that genuinely
 * have orders in flight (NGX funds, for one, reports 15 in flight with every
 * aging band empty and a max of 0). Zero there means "could not be computed",
 * not "waited no time at all", and rendering it as "0.0s" beside 15 waiting
 * orders reads as a broken screen rather than missing data.
 *
 * One place decides, so the hero, the section header and the section body
 * cannot disagree.
 */
export const settlementWait = (oldestMs, inFlightCount) => {
  if (!inFlightCount)
    return {text: 'nothing waiting', short: '—', known: false};
  if (oldestMs == null || oldestMs === 0)
    return {text: 'wait time unavailable', short: '—', known: false};
  return {
    text: `longest waiting ${durationLong(oldestMs)}`,
    short: duration(oldestMs),
    known: true,
  };
};

const Stat = ({label, value, hint, tone}) => (
  <div className={`${styles.stat} ${tone ? styles[`stat${tone}`] : ''}`}>
    <span className={styles.statLabel}>{label}</span>
    <span className={styles.statValue}>{value}</span>
    {hint && <span className={styles.statHint}>{hint}</span>}
  </div>
);

/* ---------- flow ---------------------------------------------------------- */

/**
 * ⚠️ Two rules live here.
 *
 * 1. Stage counts OVERLAP and do not sum — a partly filled order is genuinely
 *    both pending and filled. So: no pie chart, no derived "other" bucket, and
 *    `partiallyFilledOrderCount` is shown as the overlap rather than hidden.
 * 2. `notional` means different things per stage: intent on `pending`, traded
 *    value everywhere else. They are labelled so they cannot be read as one
 *    running total, and never added.
 */
export const FlowSection = ({flow}) => {
  if (!flow) return null;

  const stages = [
    {
      key: 'pending',
      label: 'Pending',
      stage: flow.pending,
      money: 'Pending (intent)',
      moneyHint: 'what outstanding orders are asking for',
      qtyHint: 'residual — the unfilled part',
    },
    {
      key: 'everFilled',
      label: 'Ever filled',
      stage: flow.everFilled,
      money: 'Traded value',
      moneyHint: 'what actually changed hands',
    },
    {
      key: 'inFlight',
      label: 'In flight',
      stage: flow.inFlight,
      money: 'Traded value',
      moneyHint: 'filled, not yet settled — money committed, not delivered',
    },
    {
      key: 'settled',
      label: 'Settled',
      stage: flow.settled,
      money: 'Traded value',
      moneyHint: 'what actually changed hands',
    },
  ];

  return (
    <>
      <p className={styles.sectionNote}>
        These stages overlap — an order that partly filled is pending for the
        rest and filled for the part that executed, so the counts do not add up
        to the total.
      </p>
      <div className={styles.stageGrid}>
        {stages.map(({key, label, stage, money, moneyHint, qtyHint}) => (
          <div key={key} className={styles.stageCard}>
            <div className={styles.stageHead}>
              <h4>{label}</h4>
              <span className={styles.stageCount}>
                {count(stage?.orderCount)}
              </span>
            </div>
            <div className={styles.stageSplit}>
              <span>{count(stage?.buyOrderCount)} buy</span>
              <span>{count(stage?.sellOrderCount)} sell</span>
            </div>
            <div className={styles.stageRow}>
              <span className={styles.stageRowLabel}>
                Quantity
                {qtyHint && (
                  <Tooltip title={qtyHint}>
                    <span className={styles.info}>?</span>
                  </Tooltip>
                )}
              </span>
              <span className={styles.stageRowValue}>
                {quantity(stage?.quantity)}
              </span>
            </div>
            <div className={styles.stageMoney}>
              <span className={styles.stageRowLabel}>
                {money}
                <Tooltip title={moneyHint}>
                  <span className={styles.info}>?</span>
                </Tooltip>
              </span>
              <Money value={stage?.notional} />
            </div>
          </div>
        ))}
      </div>

      <div className={styles.statRow}>
        <Stat
          label="Partly filled"
          value={count(flow.partiallyFilledOrderCount)}
          hint="in both Pending and Ever filled"
        />
        <div className={styles.stat}>
          <span className={styles.statLabel}>Turnover</span>
          <Money value={flow.turnover} />
          <span className={styles.statHint}>total traded value in scope</span>
        </div>
        <div className={styles.stat}>
          <span className={styles.statLabel}>Imbalance</span>
          <Money value={flow.imbalance} signed />
          <span className={styles.statHint}>
            buy minus sell — positive is net buying
          </span>
        </div>
      </div>
    </>
  );
};

/* ---------- fees ---------------------------------------------------------- */

/**
 * ⚠️ `charged` is revenue. `quoted` is NOT — it includes the floating reserve,
 * which is capital held against price movement and released if unused. The gap
 * between them is normal and can be several times the charged amount, so it is
 * never presented as a shortfall.
 *
 * In `byType`, FLOATING has an empty `charged` list. That renders "n/a", never
 * a zero, because there is no charged counterpart for a reserve.
 */
export const FeesSection = ({fees, singleCurrency}) => {
  if (!fees) return null;

  return (
    <>
      <div className={styles.statRow}>
        <div className={`${styles.stat} ${styles.statAccent}`}>
          <span className={styles.statLabel}>Charged</span>
          <Money value={fees.charged} />
          <span className={styles.statHint}>revenue</span>
        </div>
        <div className={styles.stat}>
          <span className={styles.statLabel}>Quoted</span>
          <Money value={fees.quoted} />
          <span className={styles.statHint}>
            withheld at placement — not revenue
          </span>
        </div>
        <div className={styles.stat}>
          <span className={styles.statLabel}>Floating reserve held</span>
          <Money value={fees.floatingReserveHeld} />
          <span className={styles.statHint}>
            capital held, not income — released if unused
          </span>
        </div>
        <div className={styles.stat}>
          <span className={styles.statLabel}>Returned to wallet</span>
          <Money value={fees.returnedToWallet} />
          <span className={styles.statHint}>after the order completed</span>
        </div>
      </div>

      <div className={styles.statRow}>
        <div className={styles.stat}>
          <span className={styles.statLabel}>Waived</span>
          <Money value={fees.waived} />
          <span className={styles.statHint}>
            on {count(fees.waivedOrderCount)} order
            {fees.waivedOrderCount === 1 ? '' : 's'}
          </span>
        </div>
        <Stat
          label="Effective rate"
          value={
            fees.effectiveRateBps == null
              ? '—'
              : `${fees.effectiveRateBps.toFixed(1)} bps`
          }
          hint={
            fees.effectiveRateBps == null
              ? singleCurrency
                ? 'no turnover to rate'
                : 'pick one currency to see this'
              : 'charged ÷ turnover'
          }
        />
      </div>

      {fees.byType?.length > 0 && (
        <div className={styles.feeTable}>
          <div className={styles.feeHead}>
            <span>Fee type</span>
            <span>Quoted</span>
            <span>Charged</span>
          </div>
          {fees.byType.map(row => (
            <div key={row.feeType} className={styles.feeRow}>
              <span className={styles.feeType}>{row.feeType}</span>
              <Money value={row.quoted} />
              {/* A reserve has no charged counterpart — "n/a", never 0. */}
              {row.feeType === 'FLOATING' && !row.charged?.length ? (
                <span className={styles.moneyEmpty}>n/a</span>
              ) : (
                <Money value={row.charged} />
              )}
            </div>
          ))}
        </div>
      )}
    </>
  );
};

/* ---------- latency ------------------------------------------------------- */

/**
 * ⚠️ `averageMs` is a mean, not a typical value — a few very slow orders drag
 * it up. Labelled "average", never "typical". `count` is shown beside it
 * because an average over 3 orders and one over 3,000 are different claims.
 */
export const LatencySection = ({latency}) => {
  if (!latency) return null;

  const rows = [
    {
      key: 'timeToFill',
      label: 'Time to fill',
      hint: 'created → last fill',
      stats: latency.timeToFill,
    },
    {
      key: 'timeToSettle',
      label: 'Time to settle',
      hint: 'created → settled',
      stats: latency.timeToSettle,
    },
    {
      key: 'fillToSettle',
      label: 'Fill to settle',
      hint: 'last fill → settled — the settlement leg on its own',
      stats: latency.fillToSettle,
    },
  ];

  return (
    <div className={styles.latencyTable}>
      <div className={styles.latencyHead}>
        <span>Stage</span>
        <span>Average</span>
        <span>Fastest</span>
        <span>Slowest</span>
        <span>Orders</span>
      </div>
      {rows.map(({key, label, hint, stats}) => (
        <div key={key} className={styles.latencyRow}>
          <span className={styles.latencyLabel}>
            {label}
            <Tooltip title={hint}>
              <span className={styles.info}>?</span>
            </Tooltip>
          </span>
          <span className={styles.latencyValue}>
            {duration(stats?.averageMs)}
          </span>
          <span className={styles.latencyMuted}>
            {stats?.minMs == null ? '—' : duration(stats.minMs)}
          </span>
          <span className={styles.latencyMuted}>{duration(stats?.maxMs)}</span>
          <span className={styles.latencyMuted}>{count(stats?.count)}</span>
        </div>
      ))}
    </div>
  );
};

/* ---------- settlement ---------------------------------------------------- */

/**
 * The aging bands DO partition — every filled-but-unsettled order sits in
 * exactly one — so unlike the flow stages these are safe to show as one bar.
 * The total equals `flow.inFlight.orderCount`.
 */
export const SettlementSection = ({settlement, inFlightCount}) => {
  if (!settlement) return null;

  const wait = settlementWait(settlement.oldestUnsettledMs, inFlightCount);
  const aging = settlement.aging ?? {};
  const bands = [
    {key: 'underOneDay', label: 'Under 1 day', value: aging.underOneDay},
    {key: 'oneToThreeDays', label: '1–3 days', value: aging.oneToThreeDays},
    {key: 'threeToSevenDays', label: '3–7 days', value: aging.threeToSevenDays},
    {
      key: 'sevenToThirtyDays',
      label: '7–30 days',
      value: aging.sevenToThirtyDays,
    },
    {key: 'overThirtyDays', label: 'Over 30 days', value: aging.overThirtyDays},
  ];
  const total = bands.reduce((sum, band) => sum + (band.value ?? 0), 0);

  return (
    <>
      <div className={styles.statRow}>
        <Stat
          label="Awaiting settlement"
          value={count(inFlightCount ?? total)}
          hint="filled, not yet settled"
        />
        <Stat
          label="Longest wait"
          value={wait.known ? durationLong(settlement.oldestUnsettledMs) : '—'}
          hint={
            wait.known
              ? 'how long the oldest fill has been waiting to settle'
              : wait.text
          }
          tone={
            wait.known && settlement.oldestUnsettledMs > 7 * 24 * 3600 * 1000
              ? 'Warn'
              : null
          }
        />
      </div>

      {total > 0 ? (
        <>
          <div className={styles.agingBar}>
            {bands.map(band =>
              band.value ? (
                <Tooltip
                  key={band.key}
                  title={`${band.label}: ${count(band.value)}`}>
                  <span
                    className={`${styles.agingSegment} ${
                      styles[`aging_${band.key}`]
                    }`}
                    style={{width: `${(band.value / total) * 100}%`}}
                  />
                </Tooltip>
              ) : null,
            )}
          </div>
          <div className={styles.agingLegend}>
            {bands.map(band => (
              <span key={band.key} className={styles.agingLegendItem}>
                <span
                  className={`${styles.agingDot} ${
                    styles[`aging_${band.key}`]
                  }`}
                />
                {band.label}
                <strong>{count(band.value)}</strong>
              </span>
            ))}
          </div>
        </>
      ) : (
        <p className={styles.sectionNote}>Nothing is awaiting settlement.</p>
      )}
    </>
  );
};

/* ---------- quality ------------------------------------------------------- */

/**
 * `fillRate` measures against what the user ASKED for, not the order's current
 * size — an order that fills short is rewritten down, so measuring against its
 * current size would score every one of them 100%.
 *
 * `slippage` is signed: positive is price improvement on a buy.
 */
export const QualitySection = ({quality}) => {
  if (!quality) return null;

  return (
    <>
      <div className={styles.statRow}>
        <Stat
          label="Fill rate"
          value={
            quality.fillRate == null
              ? '—'
              : `${(quality.fillRate * 100).toFixed(1)}%`
          }
          hint="filled ÷ requested units"
        />
        <Stat
          label="Requested"
          value={quantity(quality.requestedQuantity)}
          hint="units asked for"
        />
        <Stat
          label="Filled"
          value={quantity(quality.filledQuantity)}
          hint="units executed"
        />
        <Stat
          label="Shortfall"
          value={quantity(quality.shortfallQuantity)}
          hint="units asked for and never filled"
        />
      </div>

      <div className={styles.statRow}>
        <Stat
          label="Filled in full"
          value={count(quality.completeFillOrderCount)}
        />
        <Stat
          label="Under-filled"
          value={count(quality.underFilledOrderCount)}
          hint="filled fewer units than asked"
        />
        <div className={styles.stat}>
          <span className={styles.statLabel}>Slippage</span>
          <Money value={quality.slippage} signed />
          <span className={styles.statHint}>
            positive is price improvement on a buy
          </span>
        </div>
        <Stat
          label="Order mix"
          value={`${count(quality.marketOrderCount)} / ${count(
            quality.limitOrderCount,
          )}`}
          hint="market / limit"
        />
      </div>
    </>
  );
};

/* ---------- failures ------------------------------------------------------ */

export const FailuresSection = ({failures}) => {
  if (!failures) return null;

  const items = [
    {label: 'Rejected', value: failures.rejectedOrderCount},
    {label: 'Cancelled', value: failures.cancelledOrderCount},
    {label: 'Expired', value: failures.expiredOrderCount},
    {label: 'Errored', value: failures.erroredOrderCount},
    {label: 'Retried', value: failures.retriedOrderCount},
    {label: 'On hold', value: failures.onHoldOrderCount},
    {
      label: 'Re-dispatched',
      value: failures.reDispatchedOrderCount,
      hint: 'sent to a different broker after a first attempt',
    },
  ];

  return (
    <>
      <div className={styles.statRow}>
        {items.map(item => (
          <Stat
            key={item.label}
            label={item.label}
            value={count(item.value)}
            hint={item.hint}
            tone={item.value > 0 ? 'Warn' : null}
          />
        ))}
      </div>
      <p className={styles.sectionNote}>
        Counts primary orders only. Leftovers from partly-filled-then-cancelled
        orders are excluded unless derivatives are included.
      </p>
    </>
  );
};

/* ---------- participation ------------------------------------------------- */

/**
 * These are distinct counts across the whole scope, not per currency — the doc
 * is explicit that they must not be broken down by currency from this
 * response.
 */
export const ParticipationSection = ({participation}) => {
  if (!participation) return null;

  return (
    <>
      <div className={styles.statRow}>
        <Stat label="Traders" value={count(participation.traderCount)} />
        <Stat
          label="Instruments"
          value={count(participation.instrumentCount)}
        />
        <Stat
          label="Brokers"
          value={count(participation.brokerCount)}
          hint="currently holding orders"
        />
      </div>
      <div className={styles.statRow}>
        <Stat
          label="Recurring"
          value={count(participation.recurringInvestmentOrderCount)}
        />
        <Stat
          label="Affiliate"
          value={count(participation.affiliateOrderCount)}
        />
        <Stat
          label="Collection"
          value={count(participation.collectionOrderCount)}
        />
        <Stat label="Bundle" value={count(participation.bundleOrderCount)} />
        <Stat label="Reward" value={count(participation.rewardOrderCount)} />
      </div>
    </>
  );
};

/* ---------- window -------------------------------------------------------- */

const DATE_FIELD_LABELS = {
  CREATED_AT: 'created',
  PLACED_AT: 'placed',
  LAST_FILL_DATE: 'last filled',
  SETTLEMENT_DATE: 'settled',
};

/**
 * What the server actually applied, plus its own caveats.
 *
 * `notes` are generated from the filter that was sent and explain scope limits
 * a reader cannot infer from the numbers — including the one saying broker
 * volume is not attributable.
 */
export const WindowSummary = ({window: appliedWindow, generatedAt}) => {
  if (!appliedWindow) return null;

  const range =
    appliedWindow.startDate || appliedWindow.endDate
      ? `${
          appliedWindow.startDate
            ? moment(appliedWindow.startDate).format('D MMM YYYY')
            : 'the beginning'
        } → ${
          appliedWindow.endDate
            ? moment(appliedWindow.endDate).format('D MMM YYYY')
            : 'now'
        }`
      : 'all time';

  return (
    <div className={styles.windowStrip}>
      <div className={styles.windowLine}>
        <span className={styles.windowLabel}>Covering</span>
        <span className={styles.windowValue}>
          orders {DATE_FIELD_LABELS[appliedWindow.dateField] ?? 'created'}{' '}
          {range}
        </span>
        {generatedAt && (
          <span className={styles.windowGenerated}>
            as of {moment(generatedAt).format('D MMM, HH:mm')}
          </span>
        )}
      </div>
      {appliedWindow.notes?.length > 0 && (
        <ul className={styles.windowNotes}>
          {appliedWindow.notes.map(note => (
            <li key={note}>{note}</li>
          ))}
        </ul>
      )}
    </div>
  );
};
